<template>
  <section class="institutional-page-hero">
    <div class="institutional-page-hero__inner">
      <div class="institutional-page-hero__navigation">
        <NuxtLink
          :to="localePath('/')"
          class="institutional-page-hero__back-link">
          <PrePeersIcon name="arrow-left" />
          {{ $t('institutionalPageHero.backToHome') }}
        </NuxtLink>
        <span
          class="institutional-page-hero__separator"
          aria-hidden="true" />
        <PrepeersBreadcrumb
          :items="breadcrumbItems"
          :container="false"
          inline
          class="institutional-page-hero__breadcrumb" />
      </div>

      <h1 class="institutional-page-hero__title">
        {{ titleParts.beforeHighlight
        }}<span
          v-if="titleParts.highlight"
          class="text-gradient"
          >{{ titleParts.highlight }}</span
        >{{ titleParts.afterHighlight }}
      </h1>

      <slot />
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { ComputedRef } from 'vue'
import type {
  PrePeersInstitutionalPageHeroProps,
  PrePeersInstitutionalPageHeroTitleParts,
} from '~/types/PrePeersInstitutionalPageHero'
import type { PrepeersBreadcrumbItem } from '~/types/PrepeersBreadcrumb'
import { computed } from 'vue'

import PrePeersIcon from '~/components/icons/PrePeersIcon.vue'
import PrepeersBreadcrumb from '~/components/navigation/PrepeersBreadcrumb.vue'

const props: PrePeersInstitutionalPageHeroProps = defineProps({
  title: {
    type: String,
    required: true,
  },
  highlightedTitlePart: {
    type: String,
    default: '',
  },
  breadcrumbLabel: {
    type: String,
    default: '',
  },
})

const { t }: { t: (key: string) => string } = useI18n()
const localePath: ReturnType<typeof useLocalePath> = useLocalePath()

const breadcrumbItems: ComputedRef<PrepeersBreadcrumbItem[]> = computed(
  (): PrepeersBreadcrumbItem[] => [
    { label: t('breadcrumb.prepeers'), to: '/' },
    { label: props.breadcrumbLabel || props.title },
  ],
)

const titleParts: ComputedRef<PrePeersInstitutionalPageHeroTitleParts> = computed(
  (): PrePeersInstitutionalPageHeroTitleParts => {
    const highlight: string = props.highlightedTitlePart ?? ''
    const highlightIndex: number = highlight ? props.title.lastIndexOf(highlight) : -1
    if (highlightIndex === -1) {
      return { beforeHighlight: props.title, highlight: '', afterHighlight: '' }
    }

    return {
      beforeHighlight: props.title.slice(0, highlightIndex),
      highlight,
      afterHighlight: props.title.slice(highlightIndex + highlight.length),
    }
  },
)
</script>

<style scoped>
.institutional-page-hero {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  border-bottom: 1px solid var(--border-subtle);
}

.institutional-page-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background:
    radial-gradient(ellipse 55% 95% at 88% -15%, rgba(3, 160, 241, 0.16) 0%, transparent 65%),
    radial-gradient(ellipse 45% 85% at 0% -25%, rgba(63, 63, 216, 0.16) 0%, transparent 62%);
}

.institutional-page-hero__inner {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-5);
  width: 100%;
  max-width: min(var(--container-lg), calc(100% - 2 * var(--space-6)));
  margin-inline: auto;
  padding-block: var(--space-10) var(--space-12);
}

.institutional-page-hero__navigation {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3) var(--space-4);
}

.institutional-page-hero__back-link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: 0.4rem var(--space-4) 0.4rem var(--space-3);
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-secondary);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-full);
  transition:
    color var(--duration-fast) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out),
    background var(--duration-fast) var(--ease-out);
}

.institutional-page-hero__back-link:hover,
.institutional-page-hero__back-link:focus-visible {
  color: var(--text-primary);
  border-color: var(--border-default);
  background: rgba(3, 160, 241, 0.08);
}

.institutional-page-hero__separator {
  width: 1px;
  height: 18px;
  background: rgba(255, 255, 255, 0.12);
}

.institutional-page-hero__breadcrumb {
  padding: 0;
}

.institutional-page-hero__breadcrumb :deep(.breadcrumb__list) {
  font-size: var(--text-sm);
}

.institutional-page-hero__title {
  font-size: clamp(2rem, 4.2vw, 3rem);
  font-weight: 800;
  letter-spacing: -0.035em;
  line-height: 1.1;
}

@media (max-width: 768px) {
  .institutional-page-hero__inner {
    gap: var(--space-4);
    padding-block: var(--space-6) var(--space-8);
  }

  .institutional-page-hero__navigation {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .institutional-page-hero__separator {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .institutional-page-hero__back-link {
    transition: none;
  }
}

@media print {
  .institutional-page-hero__navigation {
    display: none;
  }
}
</style>
